import { useEffect, useRef, useState } from "react";
import { useLenis } from "lenis/react";

const SESSION_KEY = "karams_intro_seen";
const FADE_MS = 700;

// Minimalist cinematic camera-screen intro. Fades smoothly into the homepage.
// Shows once per browser session (unless `force` for admin preview).
// Uses plain timers + CSS transitions so removal is guaranteed (no reliance on rAF).
export const Intro = ({ config, force = false, onDone }) => {
  const enabled = force || (config && config.enabled !== false);
  const durationSec = Math.max(0.3, Number(config?.durationSec) || 1.5);
  const text = config?.text || "Bienvenue chez Karams Studio";
  const color = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(config?.textColor || "") ? config.textColor : "#FFFFFF";

  const shouldShow = () => {
    if (!enabled) return false;
    if (force) return true;
    try { return !sessionStorage.getItem(SESSION_KEY); } catch { return true; }
  };

  const [mounted, setMounted] = useState(shouldShow);
  const [shown, setShown] = useState(false); // entrance
  const [leaving, setLeaving] = useState(false); // exit fade

  // Keep the latest Lenis instance (it may be null on first render, then resolves).
  const lenis = useLenis();
  const lenisRef = useRef(null);
  lenisRef.current = lenis;

  useEffect(() => {
    if (!mounted) return;
    if (!force) { try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {} }

    // Lock scrolling while the intro overlay is visible.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (lenisRef.current) lenisRef.current.stop();

    // Release the lock as soon as the intro FINISHES (not only on unmount) and
    // re-sync Lenis so it recomputes the scrollable height (fixes iOS first-load block).
    let released = false;
    const unlock = () => {
      if (released) return;
      released = true;
      document.body.style.overflow = prevOverflow;
      const l = lenisRef.current;
      if (l) { try { l.start(); l.resize(); } catch {} }
      // Belt-and-suspenders: force any scroll observers to recompute dimensions.
      try { window.dispatchEvent(new Event("resize")); } catch {}
    };

    const tIn = setTimeout(() => setShown(true), 30);
    const tOut = setTimeout(() => setLeaving(true), durationSec * 1000);
    const tDone = setTimeout(() => { unlock(); setMounted(false); }, durationSec * 1000 + FADE_MS);

    return () => { clearTimeout(tIn); clearTimeout(tOut); clearTimeout(tDone); unlock(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (!mounted && onDone) onDone(); }, [mounted, onDone]);

  if (!mounted) return null;

  return (
    <div
      data-testid="site-intro"
      className="fixed inset-0 z-[10050] bg-[#050505] flex items-center justify-center overflow-hidden transition-opacity ease-out"
      style={{ opacity: leaving ? 0 : 1, transitionDuration: `${FADE_MS}ms` }}>

      {/* Camera framing brackets */}
      <span className="pointer-events-none absolute top-6 left-6 h-8 w-8 border-t border-l border-white/25 md:top-10 md:left-10 md:h-12 md:w-12" />
      <span className="pointer-events-none absolute top-6 right-6 h-8 w-8 border-t border-r border-white/25 md:top-10 md:right-10 md:h-12 md:w-12" />
      <span className="pointer-events-none absolute bottom-6 left-6 h-8 w-8 border-b border-l border-white/25 md:bottom-10 md:left-10 md:h-12 md:w-12" />
      <span className="pointer-events-none absolute bottom-6 right-6 h-8 w-8 border-b border-r border-white/25 md:bottom-10 md:right-10 md:h-12 md:w-12" />

      {/* REC indicator */}
      <div className="absolute top-7 left-14 flex items-center gap-2 md:top-11 md:left-20">
        <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
        <span className="text-[10px] tracking-[0.4em] uppercase text-white/60">REC</span>
      </div>

      {/* Center text */}
      <h2
        className="px-8 text-center font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight transition-all duration-700 ease-out"
        style={{ color, opacity: shown ? 1 : 0, transform: shown ? "scale(1)" : "scale(1.04)", letterSpacing: shown ? "0.02em" : "0.15em" }}>
        {text}
      </h2>

      {/* subtle bottom line sweep */}
      <span className="absolute bottom-16 left-1/2 -translate-x-1/2 h-px bg-electric transition-all ease-out md:bottom-24"
        style={{ width: shown ? 96 : 0, transitionDuration: `${Math.round(durationSec * 800)}ms` }} />
    </div>
  );
};
